import { lazy, Suspense, useEffect, useLayoutEffect } from 'react';
import { match, navigate, setLayerRoutes, usePath } from './lib/router.tsx';
import { getStore, OFFLINE_FIRST_LAUNCH, useAppState, useFeature, useIsOwner, useUi } from './data/store.ts';
import { TabBar, Toasts } from './components/ui.tsx';
import { config } from './config.ts';
import { GroceriesScreen } from './features/groceries/GroceriesScreen.tsx';
import { TravelScreen } from './features/trips/TravelScreen.tsx';
import { SegmentScreen } from './features/trips/SegmentScreen.tsx';
import { AttentionListScreen, AttentionScreen, EditSegmentScreen, NewSegmentScreen } from './features/trips/EditScreens.tsx';
import { SettingsScreen } from './features/settings/SettingsScreen.tsx';
import { StoresScreen } from './features/settings/StoresScreen.tsx';
import { StaplesScreen, StapleScreen } from './features/settings/StaplesScreen.tsx';
import { PeopleScreen } from './features/settings/PeopleScreen.tsx';
import { DeveloperScreen } from './features/settings/DeveloperScreen.tsx';
import { NotesLayer } from './features/notes/Notes.tsx';

/** Money is its own chunk: loaded the first time the tab opens. */
const MoneyScreen = lazy(() => import('./features/money/MoneyScreen.tsx'));
/** Language too. */
const LanguageScreen = lazy(() => import('./features/language/LanguageScreen.tsx'));
/** Laundry (docs/LAUNDRY.md): opens from a row at the top of Settings, no tab of its own. */
const LaundryScreen = lazy(() => import('./features/laundry/LaundryScreen.tsx'));

/** The first tab that's on for whoever is looking. */
function useHome(): string {
  return useFeature('groceries') ? '/groceries' : '/trips';
}

function Routes({ path }: { path: string }) {
  let m: Record<string, string> | null;
  const groceries = useFeature('groceries');
  const owner = useIsOwner();
  const money = useFeature('money');
  const language = useFeature('language');
  const laundry = useFeature('laundry');
  // A link or notification into something switched off opens the nearest screen that's on.
  if (path === '/groceries' || path.startsWith('/groceries/')) return groceries ? <GroceriesScreen /> : <GoTo path="/trips" />;
  if (path === '/money' || path.startsWith('/money/')) {
    if (!money) return <GoTo path="/trips" />;
    return (
      <Suspense fallback={<div className="screen" />}>
        <MoneyScreen path={path} />
      </Suspense>
    );
  }
  if (path === '/language' || path.startsWith('/language/')) {
    if (!language) return <GoTo path="/trips" />;
    return (
      <Suspense fallback={<div className="screen" />}>
        <LanguageScreen path={path} />
      </Suspense>
    );
  }
  if (path === '/trips') return <TravelScreen />;
  if (path === '/trips/new') return <NewSegmentScreen />;
  if (path === '/trips/attention') return <AttentionListScreen />;
  if ((m = match('/trips/attention/:emailId', path))) return <AttentionScreen emailId={m.emailId} />;
  if ((m = match('/trips/:tripId/:segmentId/edit', path))) return <EditSegmentScreen tripId={m.tripId} segmentId={m.segmentId} />;
  if ((m = match('/trips/:tripId/:segmentId', path))) return <SegmentScreen tripId={m.tripId} segmentId={m.segmentId} />;
  // Trip pages are gone; an old link to one opens the list.
  if (match('/trips/:tripId', path) || match('/trips/:tripId/new', path)) return <GoTo path="/trips" />;
  if (path === '/settings') return <SettingsScreen />;
  if (path === '/settings/laundry') {
    if (!laundry) return <GoTo path="/settings" />;
    return (
      <Suspense fallback={<div className="screen" />}>
        <LaundryScreen />
      </Suspense>
    );
  }
  if (path === '/settings/people') return <PeopleScreen />;
  if (path === '/settings/developer') return owner ? <DeveloperScreen /> : <GoTo path="/settings" />;
  if (path === '/settings/stores' || path.startsWith('/settings/staples')) {
    if (!groceries) return <GoTo path="/settings" />;
    if (path === '/settings/stores') return <StoresScreen />;
    if (path === '/settings/staples') return <StaplesScreen />;
    if ((m = match('/settings/staples/:id', path))) return <StapleScreen stapleId={m.id} />;
  }
  return null;
}

function GoTo({ path }: { path: string }) {
  useLayoutEffect(() => navigate(path, { replace: true }), [path]);
  return null;
}

const KNOWN = /^\/(groceries|trips|money|language|settings)(\/|$)/;

/** "3:42 PM" today, else "Oct 21, 3:42 PM". */
function savedTime(iso: string): string {
  const d = new Date(iso);
  const today = d.toDateString() === new Date().toDateString();
  return d.toLocaleString('en-US', today ? { hour: 'numeric', minute: '2-digit' } : { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function OfflineBanner({ savedAt }: { savedAt?: string }) {
  return (
    <div className="offline-ribbon" role="status">
      Offline{savedAt ? ` · showing what was saved at ${savedTime(savedAt)}` : ''}
    </div>
  );
}

/** Settings → Developer → "See what Natalie sees" is on: say so, and tap to stop. */
function ViewAsRibbon({ person }: { person: string }) {
  const name = useAppState((s) => s.people.find((p) => p.id === person)?.name ?? person);
  return (
    <button type="button" className="preview-ribbon view-as-ribbon" onClick={() => getStore().setViewAs(undefined)}>
      Seeing what {name} sees · <u>Stop</u>
    </button>
  );
}

export function App() {
  const path = usePath();
  const ui = useUi();
  const home = useHome();

  useEffect(() => {
    if (ui.ready && !KNOWN.test(path)) navigate(home, { replace: true });
  }, [path, home, ui.ready]);

  if (ui.error) {
    return (
      <div className="app">
        <div className="screen" style={{ justifyContent: 'center', minHeight: '100dvh' }}>
          <h1 className="h1 h1-sm">{ui.error === OFFLINE_FIRST_LAUNCH ? 'No connection' : "Couldn't load"}</h1>
          <p className="muted">{ui.error}</p>
          <button type="button" className="btn btn-primary" onClick={() => location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }
  if (!ui.ready) {
    return (
      <div className="app">
        <div className="screen" style={{ justifyContent: 'center', alignItems: 'center', minHeight: '100dvh' }}>
          <span className="eyebrow">Natangelo</span>
        </div>
      </div>
    );
  }

  const ribbons = (
    <>
      {config.isPreview && <div className="preview-ribbon">Preview with sample data · changes stay in this browser</div>}
      {ui.viewAs && <ViewAsRibbon person={ui.viewAs} />}
    </>
  );
  // A navigation stack like a native app: a booking (or any page under Travel) opens on a
  // layer of its own, over the Travel list, which stays mounted and scrolled where it was.
  // Back just removes the layer: nothing re-renders and nothing has to be scrolled back.
  const layered = isLayer(path);
  return (
    <div className={`app${ui.offline ? ' is-offline' : ''}${path.startsWith('/money') ? ' app-money' : ''}`}>
      {ribbons}
      {ui.offline && <OfflineBanner savedAt={ui.savedAt} />}
      <main inert={layered} aria-hidden={layered || undefined}>
        <Routes path={layered ? '/trips' : path} />
      </main>
      {layered && (
        <div className="layer" key={path}>
          {ribbons}
          <Routes path={path} />
        </div>
      )}
      <TabBar />
      <NotesLayer />
      <Toasts />
    </div>
  );
}

/** Pages that open over the Travel list instead of replacing it. */
function isLayer(path: string): boolean {
  return path.startsWith('/trips/');
}
setLayerRoutes(isLayer);
